import { lazy, Suspense, useState } from 'react';
import { ChevronDown, MessagesSquare } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Helper, Lead, MiniIcon, PageTitle, Panel, RowList, Row } from '@/components/ui/Surface';
import { getBridge } from '@/platform';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/store/authStore';
// Панель наладки почти никогда не открывается, поэтому в основную сборку она
// не входит: её код и разбор схем ответов грузятся только по запросу
const ApiProbePanel = lazy(() =>
  import('./ApiProbePanel').then((module) => ({ default: module.ApiProbePanel }))
);

/** Человекочитаемое состояние сессии для диагностики. */
const SESSION_LABELS: Record<string, string> = {
  disabled: 'не используется',
  idle: 'не установлена',
  authenticating: 'вход',
  authenticated: 'активна',
  registration: 'требуется регистрация',
  error: 'ошибка входа',
};

const SUPPORT_URL = (import.meta.env.VITE_SUPPORT_URL as string) || 'https://t.me/brand_doctor_support';

/**
 * Частые вопросы — редакционный текст интерфейса, а не данные пользователя,
 * поэтому он живёт в коде экрана. Отдельной ручки для него в контракте нет.
 */
const FAQ = [
  {
    id: 'access',
    question: 'Как открывается доступ к курсу?',
    answer:
      'Доступ привязан к почте, на которую оформлен курс. Она подтверждается кодом при первом входе, дальше вход выполняется автоматически через мессенджер.',
  },
  {
    id: 'profiles',
    question: 'Зачем нужны несколько профилей?',
    answer:
      'Профиль — это профессиональный контекст: своя анкета, свой курс и свои материалы. Профилей может быть не больше трёх, данные между ними не смешиваются.',
  },
  {
    id: 'homework',
    question: 'Как выполняется домашнее задание?',
    answer:
      'Задание проходит диалогом с ассистентом: он задаёт вопросы по блокам, помогает упорядочить ответ и собирает итоговый материал. Готовый ответ отправляется на проверку.',
  },
  {
    id: 'progress',
    question: 'Почему урок не отмечен пройденным сразу?',
    answer:
      'После отправки ассистент дорабатывает ответ. Отметка появляется, когда обработка завершится, — список уроков можно обновить кнопкой.',
  },
  {
    id: 'devices',
    question: 'Можно ли заниматься с нескольких устройств?',
    answer:
      'Курс открыт на одном устройстве одновременно. При входе с другого приложение сообщит об этом и предложит продолжить здесь.',
  },
];

/**
 * Сколько раз нужно нажать строку версии, чтобы открыть наладку.
 *
 * Панель наладки показывает сырые ответы бэкенда и данные запуска — врачу это
 * не нужно и выглядит поломкой. Но убрать её нельзя: именно ею поддержка
 * снимает диагностику с телефона пользователя, а адрес в мессенджере руками не
 * поправишь. Поэтому вход — по просьбе поддержки: несколько нажатий на версию.
 */
const DEBUG_TAPS = 5;

/** Экран 28. Помощь: частые вопросы, поддержка и диагностика. */
export function HelpScreen() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [taps, setTaps] = useState(0);
  const bridge = getBridge();
  const authPhase = useAuthStore((state) => state.phase);
  const sessionStatus = SESSION_LABELS[authPhase] ?? authPhase;

  return (
    <Screen subtitle="Помощь" back tab="profile">
      <PageTitle>Чем помочь?</PageTitle>
      <Lead>Ответы на частые вопросы о доступе, уроках и домашних заданиях.</Lead>

      <RowList>
        {FAQ.map((item) => {
          const open = openId === item.id;
          return (
            <div key={item.id}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenId(open ? null : item.id)}
                className="bd-focus flex w-full items-center justify-between gap-2 text-left"
              >
                <strong className="text-[13px] font-medium">{item.question}</strong>
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 text-muted transition-transform', open && 'rotate-180')}
                  aria-hidden
                />
              </button>
              {open ? <p className="mt-2 text-[12px] leading-[1.5] text-muted">{item.answer}</p> : null}
            </div>
          );
        })}
      </RowList>

      <Panel tone="tint" className="mt-3">
        <Row
          icon={
            <MiniIcon tone="lime">
              <MessagesSquare className="h-[17px] w-[17px]" aria-hidden />
            </MiniIcon>
          }
          title="Не нашли ответ?"
          subtitle="Напишите команде поддержки в мессенджере"
        />
        <Button variant="primary" block className="mt-3" onClick={() => bridge.openInternalLink(SUPPORT_URL)}>
          Написать в поддержку
        </Button>
      </Panel>

      {taps >= DEBUG_TAPS ? (
        <Suspense fallback={null}>
          <ApiProbePanel />
        </Suspense>
      ) : null}

      <button type="button" className="block w-full text-left" onClick={() => setTaps(taps + 1)}>
        <Helper>
          Версия приложения 1.0 · платформа: {bridge.name} · сессия: {sessionStatus} · сборка{' '}
          {__BUILD_ID__}
        </Helper>
      </button>
    </Screen>
  );
}
